/**
 * Vertical registry — the ONE place a new Extern Labs vertical gets wired in.
 *
 * Adding a vertical is: author a `VerticalPack`, add it here. No engine change, no new screens, no
 * new route.
 *
 * ⚠️ THIS MAP IS `Partial`, SO WIDENING `VerticalSlug` DOES NOT FORCE AN ENTRY, AND THAT IS LOAD-
 * BEARING. `explore` (the public Career Explorer lab) is a `VerticalSlug` that must NOT appear here:
 * this registry feeds the gated `/labs/[vertical]` route, the entry-gate precedence rules in
 * `use-labs-entry.ts` and the side-nav rail, all of which Experiment 3 is live on. It is registered in
 * `app/try/[vertical]/_engine/preview-packs.ts` instead, which reads this map and is never read by it.
 *
 * So: a slug's presence in `VerticalSlug` means the engine can render it. Its presence HERE means the
 * product offers it. Those are different claims and adding a pack asserts the second one.
 */

import type { VerticalPack, VerticalSlug } from '../_engine/types';
import { CONSULTING_PACK } from './consulting';
import { DATA_ANALYTICS_PACK } from './data-analytics';
import { FINANCE_PACK } from './finance';

/**
 * Data & Analytics was chosen as the second vertical over Product Management on catalog depth: the
 * exit screen recommends LIVE externships, and the catalog carries ~8 distinct data/analytics programs
 * against 2 PM ones. With two programs the archetype → recommendation mapping has nothing to
 * differentiate, and the payoff screen — the whole point of the sprint — collapses.
 *
 * Consulting & Strategy is the third vertical (PRO-372), chosen over Product Management: consulting and
 * strategy titles drew ~1.7x PM's registrations in the trailing 60 days (re-measured 2026-08-24 against
 * prod registrations; an earlier 2-3x figure counted Amazon Operational Strategy & People Analytics as
 * consulting, which this pack deliberately assigns to data) and give the exit screen several live
 * programs to differentiate, where PM still has one. It is also the largest single bucket of pSEO guide
 * traffic, roughly 13.7k sessions per fortnight against finance's 6.5k.
 *
 * NOTE the finance cutover HAPPENED on 2026-08-22: the v2 entry points (the pre-paywall overlay and the
 * checkout-success door) now route finance to `/labs/finance`, i.e. THIS pack through the engine. It was
 * exactly the routing change the pack was built to make possible. `/finance-discovery` still exists and
 * still renders its own older copy of the screens — the legacy catalog entry points go there — but it is
 * no longer what the experiment serves, and it carries neither the hand-off modal nor the in-sprint
 * trial bar. Prefer deleting it once the v2 read lands rather than maintaining two copies.
 */
export const PACKS = {
  consulting: CONSULTING_PACK,
  'data-analytics': DATA_ANALYTICS_PACK,
  finance: FINANCE_PACK,
} as const satisfies Partial<Record<VerticalSlug, VerticalPack>>;

/** Slugs with a pack authored and registered — i.e. the routes that can actually render. */
export type RegisteredVerticalSlug = keyof typeof PACKS;

/** Type guard for an untrusted route segment (`/labs/[vertical]`). */
export function isRegisteredVertical(slug: string): slug is RegisteredVerticalSlug {
  return Object.hasOwn(PACKS, slug);
}

/**
 * Resolve a route segment to its pack. Returns `null` for an unknown/unregistered slug so the route
 * can `notFound()` rather than render a sprint with no content.
 */
export function resolvePack(slug: string): VerticalPack | null {
  return isRegisteredVertical(slug) ? PACKS[slug] : null;
}
